import { useCallback, useEffect, useState } from 'react';
import { endOfDay, startOfDay } from 'date-fns';

import { getSentry } from '@pletly/feature-sentry';

import { useUser } from '%hooks';
import { PostsFilter, getFilters, resetFilters, setFilters } from '%services/postsFilter';

export const usePostsFilters = () => {
	const { id: userId } = useUser();
	const [loading, setLoading] = useState(true);
	const [value, setValue] = useState<PostsFilter>();

	const loadFilters = useCallback(async (userId: number | string) => {
		setLoading(true);
		try {
			const loadedFilters = await getFilters(userId);
			setValue(loadedFilters);
		} catch (error) {
			console.error('Failed to load filters:', error);
		} finally {
			setLoading(false);
		}
	}, []);

	useEffect(() => {
		void loadFilters(userId);
	}, [userId, loadFilters]);

	const update = useCallback(
		async (newFilters: PostsFilter) => {
			if (newFilters.publishedInLastDays === 'custom') {
				newFilters.publishedInCustomPeriod = {
					startDate: startOfDay(newFilters.publishedInCustomPeriod.startDate),
					endDate: endOfDay(newFilters.publishedInCustomPeriod.endDate),
				};
			}

			setValue(newFilters);
			try {
				await setFilters(newFilters, userId);
			} catch (error) {
				getSentry().captureException(error);
			}
		},
		[userId],
	);

	const reset = useCallback(async () => {
		setValue(undefined);
		try {
			await resetFilters(userId);
		} catch (error) {
			getSentry().captureException(error);
		}
	}, [userId]);

	if (loading) {
		return { loading: true, update };
	}

	return { value, loading, update, reset };
};
